import { useCallback } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import { useTickets } from 'contexts/TicketsContext';
import { useUserProfile } from 'contexts/UserProfileContext';
import { ActiveTabTypeE } from 'declarations/enums/tab';

import useHeader from '../components/Header/hooks/useHeader';
import useTicketsRefinement from '../components/TicketsRefinement/hooks/useTicketsRefinement';

import type { ChangeTicketsListFormValues } from './useTicketsPage';

const OPEN_FILTERS_EVENT = 'warrchirp-open-filters';

type UseTicketsPageShortcutsT = {
  handleSaveChanges: (data: Record<string, ChangeTicketsListFormValues>) => Promise<void>;
  formMethods: UseFormReturn<Record<string, ChangeTicketsListFormValues>>;
};

export function useTicketsPageShortcuts({
  handleSaveChanges,
  formMethods,
}: UseTicketsPageShortcutsT): Record<string, (() => void) | undefined> {
  const header = useHeader();
  const { setColumnPickerPopupVisibility } = useUserProfile();
  const { isEditMode, setEditMode, selectedTicketsIds, activeTab } = useTickets();
  const { handleClearAllFiltersClick } = useTicketsRefinement();

  const save = useCallback((): void => {
    formMethods.handleSubmit(handleSaveChanges)();
  }, [formMethods, handleSaveChanges]);

  const cancelEdit = useCallback((): void => {
    setEditMode(false);
  }, [setEditMode]);

  const editSelectedTickets = useCallback((): void => {
    if (selectedTicketsIds.length > 0) setEditMode(!isEditMode);
  }, [selectedTicketsIds.length, isEditMode, setEditMode]);

  const openFilters = useCallback((): void => {
    window.dispatchEvent(new CustomEvent(OPEN_FILTERS_EVENT));
  }, []);

  const reassign = useCallback((): void => {
    if (selectedTicketsIds.length > 0) header.reassignTicketPopup();
  }, [header, selectedTicketsIds.length]);

  const share = useCallback((): void => {
    if (selectedTicketsIds.length > 0) header.shareTicketPopup();
  }, [header, selectedTicketsIds.length]);

  const closeTickets = useCallback((): void => {
    if (selectedTicketsIds.length > 0) header.openCloseTicketPopup();
  }, [header, selectedTicketsIds.length]);

  return {
    save: isEditMode && selectedTicketsIds.length > 0 ? save : undefined,
    cancelEdit: isEditMode ? cancelEdit : undefined,
    editSelectedTickets: selectedTicketsIds.length > 0 ? editSelectedTickets : undefined,
    openFilters,
    clearFilters: handleClearAllFiltersClick,
    openColumnPicker: (): void => setColumnPickerPopupVisibility(true),
    exportTickets:
      activeTab === ActiveTabTypeE.Schedule
        ? header.handleExportScheduleClick
        : header.handleExportClick,
    reassign,
    newTicket: header.openNewTicketPopup,
    share,
    closeTickets,
    importSchedule: header.openImportSchedulePopup,
    viewScheduleFiles: header.handleOpenViewScheduleFilesPopup,
  };
}

export { OPEN_FILTERS_EVENT };
